import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Template from "../../components/Template";
import AlertDialog from "../../components/AlertDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import api from "../../services/api";
import config from "../../config";
import usePermission from "../../hooks/usePermission";
import useListFilterState from "../../hooks/useListFilterState";
import useRequisicaoMaisRecente from "../../hooks/useRequisicaoMaisRecente";
import { formatCurrencyFromDb } from "../../utils/masks";
import { formatDateWithoutHour } from "../../utils/date";

const FILTROS_VAZIOS = {
    descricao: "",
    categoriaId: "",
    statusId: "",
    vencimentoInicio: "",
    vencimentoFim: "",
};

const Despesas = () => {
    const navigate = useNavigate();
    const podeCriar = usePermission("CRIAR_DESPESA");
    const podeEditar = usePermission("ATUALIZAR_DESPESA");
    const podeExcluir = usePermission("EXCLUIR_DESPESA");

    const [despesas, setDespesas] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);
    const [valorTotal, setValorTotal] = useState(0);
    const [resumo, setResumo] = useState(null);
    const [carregando, setCarregando] = useState(true);

    const { filters, setFilters, pageSize, setPageSize, clearPersistedState } =
        useListFilterState("despesas", FILTROS_VAZIOS);

    const [categorias, setCategorias] = useState([]);
    const [statusList, setStatusList] = useState([]);

    const [alertMessage, setAlertMessage] = useState("");
    const [showAlertDialog, setShowAlertDialog] = useState(false);
    const [despesaParaExcluir, setDespesaParaExcluir] = useState(null);

    // Anexos (comprovantes)
    const [showAnexosModal, setShowAnexosModal] = useState(false);
    const [despesaSelecionada, setDespesaSelecionada] = useState(null);
    const [anexos, setAnexos] = useState([]);
    const [tiposDocumento, setTiposDocumento] = useState([]);
    const [anexoTipoId, setAnexoTipoId] = useState("");
    const [anexoFile, setAnexoFile] = useState(null);
    const [carregandoAnexos, setCarregandoAnexos] = useState(false);
    const [enviandoAnexo, setEnviandoAnexo] = useState(false);

    const requisicaoMaisRecente = useRequisicaoMaisRecente();

    const fetchDespesas = useCallback(async (page, size, filtrosParaUsar = filters) => {
        setCarregando(true);
        try {
            const params = { page, pageSize: size, ...filtrosParaUsar };
            // Campo vazio não vira parâmetro: "categoriaId=" chegaria como NaN
            // no backend e zeraria a listagem sem o usuário ter filtrado nada.
            Object.keys(params).forEach((chave) => {
                if (params[chave] === "" || params[chave] === null) delete params[chave];
            });

            const query = new URLSearchParams(params).toString();
            const idRequisicao = requisicaoMaisRecente.iniciar();
            const { data } = await api.get(`/despesas?${query}`);
            // Resposta atrasada de um filtro já superado: descarta.
            if (!requisicaoMaisRecente.ehAtual(idRequisicao)) return;

            setDespesas(data.records);
            setTotalRecords(data.total);
            setValorTotal(data.valorTotal || 0);
        } catch (error) {
            setAlertMessage(error.response?.data?.error || "Erro ao carregar as despesas.");
            setShowAlertDialog(true);
        } finally {
            setCarregando(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const fetchResumo = useCallback(async () => {
        try {
            const { data } = await api.get("/despesas/resumo");
            setResumo(data);
        } catch (error) { /* os cartões somem, a listagem continua */ }
    }, []);

    useEffect(() => {
        fetchDespesas(currentPage, pageSize);
        fetchResumo();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        api.get("/despesas/categorias").then(({ data }) => setCategorias(data)).catch(() => { });
        api.get("/statuses/byModule/nome/despesa").then(({ data }) => setStatusList(data)).catch(() => { });
    }, []);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const aplicarFiltros = () => {
        setCurrentPage(1);
        fetchDespesas(1, pageSize);
    };

    const limparFiltros = () => {
        setFilters(FILTROS_VAZIOS);
        clearPersistedState();
        setCurrentPage(1);
        fetchDespesas(1, pageSize, FILTROS_VAZIOS);
    };

    const confirmarExclusao = async () => {
        try {
            await api.delete(`/despesas/${despesaParaExcluir.id}`);
            setDespesaParaExcluir(null);
            fetchDespesas(currentPage, pageSize);
            fetchResumo();
        } catch (error) {
            setDespesaParaExcluir(null);
            setAlertMessage(error.response?.data?.error || "Erro ao excluir a despesa.");
            setShowAlertDialog(true);
        }
    };

    // ---- anexos -------------------------------------------------------------
    const abrirAnexos = async (despesa) => {
        setDespesaSelecionada(despesa);
        setShowAnexosModal(true);
        setAnexoTipoId("");
        setAnexoFile(null);
        setCarregandoAnexos(true);
        try {
            const [listaAnexos, tipos] = await Promise.all([
                api.get(`/despesas/${despesa.id}/anexos`),
                tiposDocumento.length ? Promise.resolve({ data: tiposDocumento }) : api.get("/tiposdocumentos"),
            ]);
            setAnexos(listaAnexos.data);
            setTiposDocumento(tipos.data);
        } catch (error) {
            setAlertMessage("Erro ao carregar os comprovantes.");
            setShowAlertDialog(true);
        } finally {
            setCarregandoAnexos(false);
        }
    };

    const enviarAnexo = async () => {
        if (!anexoTipoId || !anexoFile) {
            setAlertMessage("Selecione o tipo de documento e o arquivo.");
            setShowAlertDialog(true);
            return;
        }
        setEnviandoAnexo(true);
        try {
            const formData = new FormData();
            formData.append("tipoId", anexoTipoId);
            formData.append("arquivo", anexoFile);
            await api.post(`/despesas/${despesaSelecionada.id}/anexos`, formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            const { data } = await api.get(`/despesas/${despesaSelecionada.id}/anexos`);
            setAnexos(data);
            setAnexoTipoId("");
            setAnexoFile(null);
        } catch (error) {
            setAlertMessage(error.response?.data?.error || "Erro ao enviar o comprovante.");
            setShowAlertDialog(true);
        } finally {
            setEnviandoAnexo(false);
        }
    };

    const removerAnexo = async (anexoId) => {
        try {
            await api.delete(`/despesas/${despesaSelecionada.id}/anexos/${anexoId}`);
            setAnexos((prev) => prev.filter((a) => a.id !== anexoId));
        } catch (error) {
            setAlertMessage("Erro ao remover o comprovante.");
            setShowAlertDialog(true);
        }
    };

    // ---- paginação ----------------------------------------------------------
    const totalPages = Math.ceil(totalRecords / pageSize);
    const startRecord = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
    const endRecord = Math.min(currentPage * pageSize, totalRecords);
    const pageNumbers = [];
    for (let i = Math.max(1, currentPage - 2); i <= Math.min(totalPages, currentPage + 2); i++) {
        pageNumbers.push(i);
    }

    /** Vencida = sem baixa e com vencimento no passado. */
    const estaVencida = (despesa) =>
        !despesa.dataPagamento && new Date(despesa.dataVencimento) < new Date(new Date().toDateString());

    return (
        <Template>
            <div className="h-auto w-full p-4 md:p-8 flex flex-col gap-6">
                <div className="flex flex-wrap justify-between items-center gap-4">
                    <div>
                        <h1 className="text-stone-800 text-xl md:text-2xl font-medium">Despesas</h1>
                        <div className="text-stone-500 text-sm">Contas a pagar da empresa</div>
                    </div>
                    {podeCriar && (
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white rounded-md flex items-center gap-2"
                            onClick={() => navigate("/despesas/cadastrar")}
                        >
                            <img className="w-4 h-4" src="/images/icon/plus-circle.png" alt="" />
                            <span className="text-white text-sm font-medium">Lançar Despesa</span>
                        </button>
                    )}
                </div>

                {/* Cartões de resumo */}
                {resumo && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {resumo.porStatus.map((linha) => (
                            <div key={linha.statusId} className="bg-white border border-slate-200 rounded-md p-4">
                                <div className="text-stone-500 text-sm">{linha.status}</div>
                                <div className="text-stone-800 text-lg font-medium">
                                    {formatCurrencyFromDb(linha.valor)}
                                </div>
                                <div className="text-stone-400 text-xs">{linha.quantidade} lançamento(s)</div>
                            </div>
                        ))}
                        <div className="bg-white border border-red-200 rounded-md p-4">
                            <div className="text-red-600 text-sm font-medium">Vencidas</div>
                            <div className="text-red-700 text-lg font-medium">
                                {formatCurrencyFromDb(resumo.vencidas.valor)}
                            </div>
                            <div className="text-red-400 text-xs">{resumo.vencidas.quantidade} lançamento(s)</div>
                        </div>
                    </div>
                )}

                {/* Filtros */}
                <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-col gap-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="f-descricao" className="text-slate-700 text-sm font-medium">Descrição</label>
                            <input
                                id="f-descricao"
                                name="descricao"
                                type="text"
                                className="pl-3 pr-3 py-2 bg-white rounded-md border border-slate-300"
                                value={filters.descricao}
                                onChange={handleFilterChange}
                                onKeyDown={(e) => e.key === "Enter" && aplicarFiltros()}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="f-categoria" className="text-slate-700 text-sm font-medium">Categoria</label>
                            <select
                                id="f-categoria"
                                name="categoriaId"
                                className="pl-3 pr-3 py-2 bg-white rounded-md border border-slate-300"
                                value={filters.categoriaId}
                                onChange={handleFilterChange}
                            >
                                <option value="">Todas</option>
                                {categorias.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="f-status" className="text-slate-700 text-sm font-medium">Status</label>
                            <select
                                id="f-status"
                                name="statusId"
                                className="pl-3 pr-3 py-2 bg-white rounded-md border border-slate-300"
                                value={filters.statusId}
                                onChange={handleFilterChange}
                            >
                                <option value="">Todos</option>
                                {statusList.map((s) => <option key={s.id} value={s.id}>{s.nome}</option>)}
                            </select>
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="f-venc-inicio" className="text-slate-700 text-sm font-medium">Vence de</label>
                            <input
                                id="f-venc-inicio"
                                name="vencimentoInicio"
                                type="date"
                                className="pl-3 pr-3 py-2 bg-white rounded-md border border-slate-300"
                                value={filters.vencimentoInicio}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label htmlFor="f-venc-fim" className="text-slate-700 text-sm font-medium">até</label>
                            <input
                                id="f-venc-fim"
                                name="vencimentoFim"
                                type="date"
                                className="pl-3 pr-3 py-2 bg-white rounded-md border border-slate-300"
                                value={filters.vencimentoFim}
                                onChange={handleFilterChange}
                            />
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <div className="flex items-center gap-2 mr-auto">
                            <span className="text-stone-600 text-sm font-medium">Ver</span>
                            <select
                                aria-label="Resultados por página"
                                className="w-20 h-8 pl-2 pr-2 py-1 bg-white rounded-md border border-slate-300 text-sm text-slate-700"
                                value={pageSize}
                                onChange={(e) => {
                                    const novoTamanho = parseInt(e.target.value, 10);
                                    setPageSize(novoTamanho);
                                    setCurrentPage(1);
                                    fetchDespesas(1, novoTamanho);
                                }}
                            >
                                {[5, 10, 25, 50, 100].map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                            </select>
                            <span className="text-stone-600 text-sm font-medium">Resultados</span>
                        </div>
                        <button
                            type="button"
                            className="px-4 py-2 bg-white/40 rounded-md border border-stone-600 text-stone-600 text-sm font-medium"
                            onClick={limparFiltros}
                        >
                            Limpar
                        </button>
                        <button
                            type="button"
                            className="px-4 py-2 bg-stone-600 text-white rounded-md text-sm font-medium"
                            onClick={aplicarFiltros}
                        >
                            Filtrar
                        </button>
                    </div>
                </div>

                {/* Tabela */}
                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white border-collapse border border-gray-200">
                        <thead className="bg-stone-200">
                            <tr>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">ID</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">Descrição</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">Categoria</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">Obra</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">Valor</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">Vencimento</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">Pagamento</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">Status</th>
                                <th className="px-4 py-2 border border-slate-200 text-slate-800 text-base font-bold">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {carregando && (
                                <tr>
                                    <td colSpan={9} className="px-4 py-6 border border-slate-200 text-center text-slate-500">
                                        Carregando despesas...
                                    </td>
                                </tr>
                            )}

                            {!carregando && despesas.map((despesa) => (
                                <tr key={despesa.id}>
                                    <td className="px-4 py-2 border border-slate-200 text-slate-700">
                                        {podeEditar ? (
                                            <Link to={`/despesas/editar/${despesa.id}`} className="text-stone-600 hover:underline">
                                                {despesa.id}
                                            </Link>
                                        ) : despesa.id}
                                    </td>
                                    <td className="px-4 py-2 border border-slate-200 text-slate-700">{despesa.descricao}</td>
                                    <td className="px-4 py-2 border border-slate-200 text-slate-700">{despesa.categoria?.nome || "-"}</td>
                                    <td className="px-4 py-2 border border-slate-200 text-slate-700">
                                        {despesa.obra ? `${despesa.obra.codigoProjeto} — ${despesa.obra.nomeProjeto}` : "-"}
                                    </td>
                                    <td className="px-4 py-2 border border-slate-200 text-slate-700 whitespace-nowrap">
                                        {formatCurrencyFromDb(despesa.valor)}
                                    </td>
                                    <td className={`px-4 py-2 border border-slate-200 whitespace-nowrap ${estaVencida(despesa) ? "text-red-600 font-medium" : "text-slate-700"}`}>
                                        {formatDateWithoutHour(despesa.dataVencimento)}
                                        {/* O vermelho sozinho não pode ser a única pista: cor não é informação. */}
                                        {estaVencida(despesa) && <span className="ml-1 text-xs">(vencida)</span>}
                                    </td>
                                    <td className="px-4 py-2 border border-slate-200 text-slate-700 whitespace-nowrap">
                                        {despesa.dataPagamento ? formatDateWithoutHour(despesa.dataPagamento) : "-"}
                                    </td>
                                    <td className="px-4 py-2 border border-slate-200 text-slate-700">{despesa.status?.nome || "-"}</td>
                                    <td className="px-4 py-2 border border-slate-200">
                                        <div className="flex items-center justify-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => abrirAnexos(despesa)}
                                                className="p-1 hover:bg-stone-100 rounded"
                                                title="Comprovantes"
                                            >
                                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                                </svg>
                                            </button>
                                            {podeExcluir && (
                                                <button
                                                    type="button"
                                                    onClick={() => setDespesaParaExcluir(despesa)}
                                                    className="p-1 hover:bg-red-50 rounded"
                                                    title="Excluir despesa"
                                                >
                                                    <img className="w-4 h-4" src="/images/icon/trash-2.png" alt="Excluir" />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}

                            {!carregando && totalRecords === 0 && (
                                <tr>
                                    <td colSpan={9} className="px-4 py-6 border border-slate-200 text-center text-slate-500">
                                        Nenhuma despesa encontrada.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        {totalRecords > 0 && (
                            <tfoot>
                                <tr className="bg-stone-100">
                                    <td colSpan={4} className="px-4 py-2 border border-slate-200 text-slate-700 font-medium text-right">
                                        Total do filtro
                                    </td>
                                    <td className="px-4 py-2 border border-slate-200 text-slate-800 font-bold whitespace-nowrap">
                                        {formatCurrencyFromDb(valorTotal)}
                                    </td>
                                    <td colSpan={4} className="px-4 py-2 border border-slate-200"></td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>

                {/* Paginação */}
                <div className="self-stretch justify-end items-center gap-2.5 inline-flex">
                    <div className="grow shrink basis-0 text-slate-500 text-base font-normal">
                        Mostrando de {startRecord} até {endRecord} de {totalRecords} registros.
                    </div>
                    <div className="justify-start items-start gap-4 flex">
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-300 cursor-pointer"}`}
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(currentPage - 1)}
                            aria-label="Página anterior"
                        >
                            <img className="w-6 h-6" src="/images/icon/keyboard-arrow-left.png" alt="" />
                        </button>
                        {pageNumbers.map((page) => (
                            <button
                                key={page}
                                className={`w-10 h-10 rounded justify-center items-center flex text-base font-semibold ${page === currentPage ? "bg-stone-600 text-white" : "bg-gray-200 text-gray-600"}`}
                                onClick={() => setCurrentPage(page)}
                            >
                                {page}
                            </button>
                        ))}
                        <button
                            className={`w-10 h-10 bg-white/0 rounded border border-[#bebebe] justify-center items-center flex ${currentPage >= totalPages || totalPages === 0 ? "opacity-30 cursor-not-allowed" : "cursor-pointer"}`}
                            disabled={currentPage >= totalPages || totalPages === 0}
                            onClick={() => setCurrentPage(currentPage + 1)}
                            aria-label="Próxima página"
                        >
                            <img className="w-6 h-6" src="/images/icon/keyboard-arrow-right.png" alt="" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal de comprovantes */}
            {showAnexosModal && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4" style={{ zIndex: 60 }}>
                    <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90%] flex flex-col">
                        <div className="px-6 py-4 border-b border-slate-200 flex justify-between items-center">
                            <div>
                                <div className="text-stone-800 text-lg font-medium">Comprovantes</div>
                                <div className="text-stone-500 text-sm">{despesaSelecionada?.descricao}</div>
                            </div>
                            <button
                                type="button"
                                className="text-stone-500 hover:text-stone-800 text-xl leading-none"
                                onClick={() => setShowAnexosModal(false)}
                                aria-label="Fechar"
                            >
                                ×
                            </button>
                        </div>

                        <div className="px-6 py-4 overflow-y-auto flex flex-col gap-4">
                            {podeEditar && (
                                <div className="flex flex-wrap items-end gap-3 border-b border-slate-100 pb-4">
                                    <div className="flex flex-col gap-1.5 min-w-[180px]">
                                        <label htmlFor="anexo-tipo" className="text-slate-700 text-sm font-medium">Tipo de documento</label>
                                        <select
                                            id="anexo-tipo"
                                            className="pl-3 pr-3 py-2 bg-white rounded-md border border-slate-300"
                                            value={anexoTipoId}
                                            onChange={(e) => setAnexoTipoId(e.target.value)}
                                        >
                                            <option value="">Selecione</option>
                                            {tiposDocumento.map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-1.5">
                                        <label htmlFor="anexo-arquivo" className="text-slate-700 text-sm font-medium">Arquivo</label>
                                        <input
                                            id="anexo-arquivo"
                                            type="file"
                                            className="text-sm"
                                            onChange={(e) => setAnexoFile(e.target.files[0])}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="px-4 py-2 bg-stone-600 text-white rounded-md text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                                        onClick={enviarAnexo}
                                        disabled={enviandoAnexo}
                                    >
                                        {enviandoAnexo ? "Enviando..." : "Anexar"}
                                    </button>
                                </div>
                            )}

                            {carregandoAnexos && <div className="text-stone-500 text-sm">Carregando comprovantes...</div>}

                            {!carregandoAnexos && anexos.length === 0 && (
                                <div className="text-stone-500 text-sm py-4 text-center">
                                    Nenhum comprovante anexado a esta despesa.
                                </div>
                            )}

                            {!carregandoAnexos && anexos.map((anexo) => (
                                <div key={anexo.id} className="flex items-center justify-between gap-4 border border-slate-200 rounded-md px-3 py-2">
                                    <div className="min-w-0">
                                        <div className="text-stone-800 text-sm font-medium truncate">
                                            {anexo.tipo?.nome || "Documento"}
                                        </div>
                                        <div className="text-stone-500 text-xs truncate">
                                            Enviado por {anexo.enviadoPor?.nome || "-"} em {formatDateWithoutHour(anexo.dataEnvio)}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3 flex-none">
                                        <a
                                            href={config.urlArquivo(anexo.arquivo)}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="text-stone-600 text-sm hover:underline"
                                        >
                                            Abrir
                                        </a>
                                        {podeEditar && (
                                            <button
                                                type="button"
                                                className="text-red-600 text-sm hover:underline"
                                                onClick={() => removerAnexo(anexo.id)}
                                            >
                                                Remover
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmationModal
                isVisible={Boolean(despesaParaExcluir)}
                onConfirm={confirmarExclusao}
                onCancel={() => setDespesaParaExcluir(null)}
                message={`Deseja apagar a despesa "${despesaParaExcluir?.descricao}"? Os comprovantes anexados também serão removidos.`}
                textButtonConfirm="Sim"
            />

            {showAlertDialog && (
                <AlertDialog
                    isVisible={showAlertDialog}
                    message={alertMessage}
                    onClose={() => setShowAlertDialog(false)}
                />
            )}
        </Template>
    );
};

export default Despesas;
